import { FOUNDING_FAQ, HOME_FAQ } from "@/seo/faq";
import { HUB_FAQ, HUB_TOOLS } from "@/content/toolsHub";

export const SITE = process.env.REACT_APP_SITE_URL.replace(/\/$/, "");
export const OG_IMAGE = `${SITE}/og-image.png`;
export const OG_ALT = "AetherTags — Real shop data. Safe changes. Proven results. The Etsy SEO tool for big catalogs.";

export const POLICIES = {
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  refund: "Refund & Cancellation Policy",
  cookie: "Cookie Policy",
};

const ORG = { "@type": "Organization", "@id": `${SITE}/#organization`, name: "AetherTags", url: `${SITE}/`, logo: `${SITE}/icon-512.png` };
const WEBSITE = { "@type": "WebSite", "@id": `${SITE}/#website`, name: "AetherTags", url: `${SITE}/`, inLanguage: "en", publisher: { "@id": `${SITE}/#organization` } };
const APP = {
  "@type": "SoftwareApplication", "@id": `${SITE}/#software`, name: "AetherTags", url: `${SITE}/`,
  applicationCategory: "BusinessApplication", operatingSystem: "Web",
  description: "Etsy SEO tool for big catalogs: listing health scores, AI title and tag fixes with reasons, approval before every push, bestseller protection, one-click undo and before/after proof.",
  publisher: { "@id": `${SITE}/#organization` },
};
const crumbs = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE}${path}` })),
});
const faqLd = (items) => ({
  "@type": "FAQPage",
  mainEntity: items.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
});
const FAQ_LD = faqLd(FOUNDING_FAQ);
const TOOLS_LIST = {
  "@type": "ItemList", name: "Free Etsy SEO tools",
  itemListElement: Object.entries(HUB_TOOLS).map(([slug, t], i) => ({ "@type": "ListItem", position: i + 1, name: t.h2, url: `${SITE}/tools/${slug}` })),
};

const policyPages = Object.fromEntries(Object.entries(POLICIES).map(([slug, title]) => [`/policies/${slug}`, {
  title: `${title} | AetherTags Etsy SEO Tool`,
  description: `AetherTags ${title.toLowerCase()} (beta draft): how the Etsy SEO tool for big catalogs handles your shop data, Etsy connection and AI fixes.`,
  ld: [ORG, crumbs([["Home", "/"], [title, `/policies/${slug}`]])],
}]));

export const PAGES = {
  "/": {
    title: "Etsy SEO Tool for Big Shops — Safe Bulk Edits | AetherTags",
    description: "AetherTags is the Etsy SEO tool for big shops: Etsy listing optimization, bulk title and tag fixes, approval, backup and undo. Join the free beta.",
    preloadHero: true,
    ld: [ORG, WEBSITE, APP, faqLd(HOME_FAQ), TOOLS_LIST],
  },
  "/founding": {
    title: "Etsy SEO Tool Founding Members — Free Beta | AetherTags",
    description: "Join the free founding-member beta of the Etsy SEO tool for big catalogs: shop audit, AI title and tag fixes, one-click undo. Create your account.",
    preloadHero: true,
    ld: [ORG, FAQ_LD, crumbs([["Home", "/"], ["Founding Members", "/founding"]])],
  },
  "/login": {
    title: "Etsy SEO Tool Login | AetherTags",
    description: "Log in to AetherTags, the Etsy SEO tool for big catalogs, to review your shop audit, approve listing fixes and see proof in your own numbers.",
    ld: [ORG],
  },
  "/signup": {
    title: "Etsy SEO Tool Beta Sign Up | AetherTags",
    description: "Create your free AetherTags beta account: safe Etsy listing optimization for big shops, with bestseller protection and one-click undo. Sign up now.",
    robots: "noindex, follow",
    ld: [ORG],
  },
  ...policyPages,
  "/blog": {
    title: "Etsy SEO Guides for Big Shops | AetherTags",
    description: "Practical Etsy SEO guides: titles, all 13 tags, attributes, descriptions, seasonal timing and safe bulk editing for big Etsy catalogs.",
    ld: [ORG, crumbs([["Home", "/"], ["Guides", "/blog"]])],
  },
  "/tools": {
    title: "Free Etsy SEO Tools: Tags, Titles & Checker | AetherTags",
    description: "Free Etsy tools, no sign-up: a tag generator (13 valid tags), a title generator (3 titles of 140 chars or fewer) and a rule-based SEO checker.",
    ld: [ORG, crumbs([["Home", "/"], ["Free tools", "/tools"]]), TOOLS_LIST, faqLd(HUB_FAQ)],
  },
  "/etsy-tags": {
    title: "Etsy Tags by Product: 13-Tag Sets & Titles | AetherTags",
    description: "Etsy tags by product: ready-to-adapt 13-tag sets, example titles and attribute checklists for print-on-demand, vintage and handmade shops.",
    ld: [ORG, crumbs([["Home", "/"], ["Etsy Tags", "/etsy-tags"]])],
  },
  "/about": {
    title: "About AetherTags: Etsy SEO Tool for Big Catalogs",
    description: "Who builds AetherTags, how we write our Etsy SEO guides, and our editorial standards: Etsy-rule checks, no invented numbers, no guarantees.",
    ld: [ORG, crumbs([["Home", "/"], ["About", "/about"]])],
  },
  "/sitemap": {
    title: "Sitemap | AetherTags",
    description: "Every public AetherTags page: Etsy SEO guides, Etsy tags by product, the founding-member program, about and policies.",
    ld: [ORG, crumbs([["Home", "/"], ["Sitemap", "/sitemap"]])],
  },
};

const g = typeof window !== "undefined" ? window : globalThis;
export const contentFor = (path) => (g.__AT_CONTENT__ || {})[normalize(path)];
export const setContent = (path, data) => { g.__AT_CONTENT__ = { ...(g.__AT_CONTENT__ || {}), [normalize(path)]: data }; };

const toolMeta = (p, a) => {
  const url = `${SITE}${p}`;
  const name = a.h1.split(":")[0];
  const image = `${SITE}/images/tools/${a.slug}-og.jpg`;
  const app = { "@type": "SoftwareApplication", name, url, applicationCategory: "BusinessApplication", operatingSystem: "Web",
    description: a.meta_description, isAccessibleForFree: true, offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    image, publisher: { "@id": `${SITE}/#organization` } };
  return { title: a.title.includes("AetherTags") || a.title.length > 46 ? a.title : `${a.title} | AetherTags`, description: a.meta_description,
    robots: "index, follow", url, image, imageAlt: a.image_alt, ld: [ORG, app, crumbs([["Home", "/"], ["Free tools", "/tools"], [name, p]]), faqLd((a.faq || []).map((f) => [f.q, f.a]))] };
};

const articleMeta = (p, a) => {
  if (a.type === "tool") return toolMeta(p, a);
  const hub = a.type === "guide" ? ["Guides", "/blog"] : ["Etsy Tags", "/etsy-tags"];
  const url = `${SITE}${p}`;
  const main = {
    "@type": a.type === "guide" ? "BlogPosting" : "WebPage", headline: a.h1, name: a.h1, description: a.meta_description, url, mainEntityOfPage: url, inLanguage: "en",
    datePublished: a.published_at, dateModified: a.updated_at, image: `${SITE}/images/content/${a.category === "big-catalogs" || a.category === "basics" || a.category === "seasonal" || a.category === "tools" ? "guides" : a.category}.webp`,
    author: { "@type": "Organization", name: a.author || "AetherTags Team", url: `${SITE}/about` }, publisher: { "@id": `${SITE}/#organization` },
  };
  return { title: a.title.includes("AetherTags") || a.title.length > 46 ? a.title : `${a.title} | AetherTags`, description: a.meta_description,
    robots: "index, follow", og: "article", url,
    ld: [ORG, main, crumbs([["Home", "/"], hub, [a.type === "guide" ? a.h1 : a.product, p]]), faqLd((a.faq || []).map((f) => [f.q, f.a]))] };
};

const SHELL = { title: "AetherTags", description: "AetherTags: the safe growth system for big Etsy catalogs.", robots: "noindex, nofollow", ld: [] };
const NOT_FOUND = { title: "Page not found | AetherTags", description: "This page doesn't exist on AetherTags.", robots: "noindex", ld: [] };
export const notFoundMeta = (p) => ({ ...NOT_FOUND, url: `${SITE}${p}` });
const APP_ONLY = /^\/(app|admin|unsubscribe|etsy)(\/|$)/;

export const normalize = (path) => (path || "/").replace(/\/+$/, "") || "/";

export const metaFor = (path) => {
  const p = normalize(path);
  const page = PAGES[p];
  if (page) return { robots: "index, follow", ...page, url: `${SITE}${p === "/" ? "/" : p}` };
  const pg = p.match(/^\/(blog|etsy-tags)\/page\/([1-9]\d*)$/);
  if (pg && PAGES[`/${pg[1]}`]) {
    const hub = PAGES[`/${pg[1]}`];
    const items = contentFor(p)?.items;
    if (pg[2] === "1") return { robots: "index, follow", ...hub, url: `${SITE}/${pg[1]}` };
    if (items && Number(pg[2]) > Math.max(1, Math.ceil(items.length / 24))) return { ...NOT_FOUND, url: `${SITE}${p}` };
    return { robots: "index, follow", ...hub, title: hub.title.replace(" | AetherTags", ` – Page ${pg[2]} | AetherTags`), url: `${SITE}${p}` };
  }
  const a = /^\/(blog|etsy-tags|tools)\/[a-z0-9-]+$/.test(p) && contentFor(p);
  if (a && a.h1) return articleMeta(p, a);
  if (a && a.notFound) return { ...NOT_FOUND, url: `${SITE}${p}` };
  // Content URL still loading (no embedded data yet): stay neutral; only the API's 404 above may set noindex.
  if (typeof window !== "undefined" && /^\/(blog|etsy-tags|tools)\/[a-z0-9-]+$/.test(p)) return { ...SHELL, robots: "index, follow", url: `${SITE}${p}` };
  return { ...(APP_ONLY.test(p) ? SHELL : NOT_FOUND), url: `${SITE}${p}` };
};

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export const ldJson = (ld) => JSON.stringify({ "@context": "https://schema.org", "@graph": ld }).replace(/</g, "\\u003c");

export const headHtml = (path) => {
  const m = path ? metaFor(path) : NOT_FOUND;
  const tags = [
    `<title>${esc(m.title)}</title>`,
    `<meta name="description" content="${esc(m.description)}" />`,
    `<meta name="robots" content="${m.robots}" />`,
  ];
  if (path) {
    tags.push(
      `<link rel="canonical" href="${esc(m.url)}" />`,
      `<meta property="og:type" content="${m.og || "website"}" />`,
      `<meta property="og:site_name" content="AetherTags" />`,
      `<meta property="og:locale" content="en_US" />`,
      `<meta property="og:title" content="${esc(m.title)}" />`,
      `<meta property="og:description" content="${esc(m.description)}" />`,
      `<meta property="og:url" content="${esc(m.url)}" />`,
      `<meta property="og:image" content="${m.image || OG_IMAGE}" />`,
      `<meta property="og:image:width" content="1200" />`,
      `<meta property="og:image:height" content="630" />`,
      `<meta property="og:image:alt" content="${esc(m.imageAlt || OG_ALT)}" />`,
      `<meta name="twitter:card" content="summary_large_image" />`,
      `<meta name="twitter:title" content="${esc(m.title)}" />`,
      `<meta name="twitter:description" content="${esc(m.description)}" />`,
      `<meta name="twitter:image" content="${m.image || OG_IMAGE}" />`,
      `<meta name="twitter:image:alt" content="${esc(m.imageAlt || OG_ALT)}" />`,
    );
    if (m.preloadHero) tags.push(`<link rel="preload" as="image" href="/images/aurora.webp" type="image/webp" fetchpriority="high" />`);
    if (m.ld.length) tags.push(`<script type="application/ld+json" id="seo-ld">${ldJson(m.ld)}</script>`);
  }
  return tags.join("\n        ");
};
